/**
 * Flash Sales utilities.
 *
 * Flash sale data stored in listing.publicData.flashSale:
 * {
 *   startAt: ISO string,
 *   endAt: ISO string,
 *   discountPercent: number (1-99),
 *   originalAmountCents: number,
 *   currency: string
 * }
 */

/**
 * Check if a flash sale is currently active.
 * @param {Object} flashSale
 * @returns {boolean}
 */
export const isFlashSaleActive = (flashSale) => {
  if (!flashSale || !flashSale.startAt || !flashSale.endAt) return false;
  const now = Date.now();
  return now >= new Date(flashSale.startAt).getTime() &&
         now <= new Date(flashSale.endAt).getTime();
};

/**
 * Calculate discounted price.
 * @param {number} originalAmountCents
 * @param {number} discountPercent — 1-99
 * @returns {number} — discounted amount in cents
 */
export const applyFlashSaleDiscount = (originalAmountCents, discountPercent) => {
  if (!discountPercent || discountPercent <= 0 || discountPercent >= 100) {
    return originalAmountCents;
  }
  const discount = Math.round(originalAmountCents * (discountPercent / 100));
  return originalAmountCents - discount;
};

/**
 * Get remaining time in milliseconds.
 * @param {Object} flashSale
 * @returns {number} — ms until sale ends, or 0 if ended
 */
export const getRemainingMs = (flashSale) => {
  if (!flashSale?.endAt) return 0;
  return Math.max(0, new Date(flashSale.endAt).getTime() - Date.now());
};

/**
 * Format remaining time as "Xh Ym" or "Xm Ys".
 * @param {number} ms
 * @returns {string}
 */
export const formatTimeRemaining = (ms) => {
  if (ms <= 0) return 'Ended';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

/**
 * Validate flash sale params.
 * @param {Object} params
 * @returns {string|null} — error message or null
 */
export const validateFlashSale = ({ startAt, endAt, discountPercent }) => {
  if (!startAt || !endAt) return 'Start and end time are required';
  if (new Date(endAt) <= new Date(startAt)) return 'End time must be after start time';
  if (new Date(startAt) < new Date(Date.now() - 60000)) return 'Start time cannot be in the past';
  if (!discountPercent || discountPercent < 1 || discountPercent > 99) {
    return 'Discount must be between 1% and 99%';
  }
  return null;
};
